/**
 * Utility functions for checking visit activation permissions
 */
import permissionsService from '../services/permissionsService';

// Roles allowed to activate/deactivate survey windows (fallback)
const ALLOWED_VISIT_ACTIVATION_ROLES = ['Study Coordinator', 'Investigator'];

/**
 * Check if user can activate/deactivate visits based on their permissions
 * @param {Object} user - User object with role and permissions information
 * @returns {boolean} - True if user can activate visits
 */
export const canActivateVisits = (user) => {
  if (!user) {
    return false;
  }
  
  // Check if user is a superuser (superusers can do everything)
  if (user.is_superuser) {
    return true;
  }
  
  // Check for specific permission (preferred method)
  const hasPermission = user.permissions && user.permissions.includes('can_activate_survey_window');
  
  // Check for allowed role (fallback)
  // user.role is a string, not an object, so check it directly
  const hasRole = user.role && ALLOWED_VISIT_ACTIVATION_ROLES.includes(user.role);
  
  // User can activate if they have EITHER the permission OR the role
  return hasPermission || hasRole;
};

/**
 * Check whether visit activation controls are valid for this participant.
 * Permission alone is not enough: deactivation suspends unfinished work and
 * the API intentionally rejects attempts to activate it.
 * @param {Object} user - User object with role and permissions information
 * @param {boolean} participantIsActive - Current participant account status
 * @returns {boolean} - True when controls may be shown and used
 */
export const canChangeParticipantVisitActivation = (user, participantIsActive) => {
  return participantIsActive === true && canActivateVisits(user);
};

/**
 * Async version that checks permissions via API
 * @param {Object} user - User object
 * @returns {Promise<boolean>} - True if user can activate visits
 */
export const canActivateVisitsAsync = async (user) => {
  if (!user) {
    return false;
  }
  
  // Check if user is a superuser (superusers can do everything)
  if (user.is_superuser) {
    return true;
  }
  
  try {
    // Check via API for most accurate result
    const hasPermission = await permissionsService.hasPermission('can_activate_survey_window');
    if (hasPermission) {
      return true;
    }
  } catch (error) {
    console.warn('Error checking activation permission via API, falling back to local check:', error);
  }
  
  // Fallback to local permission and role checks
  return canActivateVisits(user);
};

/**
 * Check if the current user can view activation data
 * This is the same as canActivateVisits for now, but separated for potential future differences
 * @param {Object} user - The user object from AuthContext
 * @returns {boolean} - True if user can view activation data
 */
export const canViewActivationData = (user) => {
  return canActivateVisits(user);
};

export default {
  canActivateVisits,
  canChangeParticipantVisitActivation,
  canActivateVisitsAsync,
  canViewActivationData,
  ALLOWED_VISIT_ACTIVATION_ROLES
};
